import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthProvider';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Calendar, 
  CreditCard, 
  AlertTriangle, 
  Trash2, 
  LogOut,
  RefreshCw,
  Crown,
  XCircle,
  CheckCircle,
  Palette,
  Link2
} from 'lucide-react';
import { BrandingSettings } from '../profile/BrandingSettings';
import { AccountSlugSettings } from '../profile/AccountSlugSettings';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

type TabType = 'account' | 'subscription' | 'url' | 'branding' | 'danger';

export const Profile: React.FC = () => {
  const { user, logout, session } = useAuth();
  const { subscription, planLimits, refresh } = usePlanLimits();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const [loading, setLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [brandingAccess, setBrandingAccess] = useState<{ hasAccess: boolean; plan: string } | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!session?.access_token || !user?.id) return;

      try {
        const response = await fetch(`${API_BASE}/api/team/members`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const members = await response.json();
          const currentUser = members.find((m: any) => m.id === user.id);
          if (currentUser) {
            setUserRole(currentUser.role_type);
          }
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [session, user]);

  // Check branding access and sync usage
  const checkBrandingAccess = async () => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE}/api/branding/check-access`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const accessData = await response.json();
        setBrandingAccess(accessData);
      }
    } catch (error) {
      console.error('Error checking branding access:', error);
    }
  };

  // Sync usage statistics when loading profile
  const syncUsage = async () => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE}/api/billing/subscription/sync-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        console.log('✅ Usage statistics synced');
        // Refresh subscription data after sync
        await refresh();
      }
    } catch (error) {
      console.error('Error syncing usage:', error);
    }
  };

  useEffect(() => {
    checkBrandingAccess();
    // Sync usage statistics on profile load
    syncUsage();
  }, [session]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Cancel subscription (but keep account)
  const handleCancelSubscription = async () => {
    if (!session?.access_token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/billing/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: cancelReason })
      });

      if (response.ok) {
        alert('Ihr Abonnement wurde gekündigt. Sie haben weiterhin Zugang bis zum Ende der aktuellen Abrechnungsperiode.');
        setShowCancelModal(false);
        await refresh();
      } else {
        const error = await response.json();
        alert('Fehler beim Kündigen: ' + error.message);
      }
    } catch (error) {
      alert('Fehler beim Kündigen der Subscription');
    } finally {
      setLoading(false);
    }
  };

  // Delete account entirely
  const handleDeleteAccount = async () => {
    if (!session?.access_token) return;
    if (deleteConfirmText !== 'LÖSCHEN') {
      alert('Bitte geben Sie "LÖSCHEN" ein, um zu bestätigen.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/account/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        alert('Ihr Account wurde gelöscht. Sie werden nun ausgeloggt.');
        await logout();
        navigate('/');
      } else {
        const error = await response.json();
        alert('Fehler beim Löschen: ' + error.message);
      }
    } catch (error) {
      alert('Fehler beim Löschen des Accounts');
    } finally {
      setLoading(false);
    }
  };

  // Open Stripe billing portal
  const handleManageBilling = async () => {
    if (!session?.access_token) return;

    setLoading(true);
    try {
      console.log('🏪 Opening billing portal...');
      
      const response = await fetch(`${API_BASE}/api/billing/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (response.ok && data.url) {
        console.log('✅ Portal URL received, redirecting...');
        window.location.href = data.url;
      } else {
        console.error('❌ Portal error:', data);
        
        // Handle specific error codes
        switch (data.code) {
          case 'NO_SUBSCRIPTION':
            alert('Sie haben noch kein Abonnement. Bitte wählen Sie zuerst einen Plan aus.');
            navigate('/qr/pricing');
            break;
          case 'FREE_PLAN':
            alert('Sie nutzen den kostenlosen Plan. Es gibt keine Zahlungen zu verwalten.');
            if (data.redirect) {
              navigate('/qr/pricing');
            }
            break;
          case 'PORTAL_NOT_CONFIGURED':
            // Show user-friendly message instead of technical setup instructions
            const userMessage = [
              '⚙️ Zahlungsportal wird eingerichtet',
              '',
              'Das Zahlungsportal ist noch nicht vollständig konfiguriert.',
              'Bitte kontaktieren Sie den Support oder versuchen Sie es später erneut.',
              '',
              'In der Zwischenzeit können Sie:',
              '• Ihren aktuellen Plan in Ihrem Profil einsehen',
              '• Bei Fragen den Support kontaktieren',
              '• Später erneut versuchen'
            ].join('\n');
            
            alert(userMessage);
            break;
          case 'CUSTOMER_CREATION_FAILED':
          case 'CUSTOMER_DELETED':
          case 'CUSTOMER_NOT_FOUND':
            alert(data.message || 'Fehler beim Zugriff auf das Zahlungskonto. Bitte kontaktieren Sie den Support.');
            break;
          case 'STRIPE_API_ERROR':
          case 'STRIPE_CONNECTION_ERROR':
            alert('Das Zahlungssystem ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut.');
            break;
          default:
            alert(data.message || data.error || 'Fehler beim Öffnen des Zahlungsportals');
        }
      }
    } catch (error) {
      console.error('❌ Network error opening billing portal:', error);
      alert('Netzwerkfehler beim Öffnen des Zahlungsportals. Bitte überprüfen Sie Ihre Internetverbindung.');
    } finally {
      setLoading(false);
    }
  };

  const getPlanDisplayName = (plan: string) => {
    const names: Record<string, string> = {
      free: 'Kostenlos',
      starter: 'Starter', 
      growth: 'Growth',
      business: 'Business'
    };
    return names[plan] || plan;
  };

  const getStatusDisplay = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return { text: 'Läuft aus', color: 'text-orange-600', bg: 'bg-orange-100', icon: XCircle };
    }
    
    switch (status) {
      case 'active':
        return { text: 'Aktiv', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle };
      case 'past_due':
        return { text: 'Überfällig', color: 'text-red-600', bg: 'bg-red-100', icon: AlertTriangle };
      case 'canceled':
        return { text: 'Gekündigt', color: 'text-gray-600', bg: 'bg-gray-100', icon: XCircle };
      default:
        return { text: status, color: 'text-gray-600', bg: 'bg-gray-100', icon: AlertTriangle };
    }
  };

  if (!user) {
    return <div>Nicht angemeldet</div>;
  }

  const statusDisplay = subscription ? 
    getStatusDisplay(subscription.status, subscription.cancel_at_period_end || false) :
    { text: 'Lädt...', color: 'text-gray-600', bg: 'bg-gray-100', icon: RefreshCw };

  const StatusIcon = statusDisplay.icon;

  // Tab configuration
  const tabs = [
    { id: 'account' as TabType, label: 'Account', icon: User },
    ...(userRole === 'owner' ? [
    { id: 'subscription' as TabType, label: 'Abonnement', icon: CreditCard },
    // { id: 'url' as TabType, label: 'Account-URL', icon: Link2 }, // Temporarily disabled
    ] : []),
    { 
      id: 'branding' as TabType, 
      label: 'Branding', 
      icon: Palette,
      disabled: !brandingAccess?.hasAccess,
      badge: brandingAccess?.hasAccess ? null : (subscription?.plan === 'business' ? null : 'Business')
    },
    { id: 'danger' as TabType, label: 'Gefahrenbereich', icon: AlertTriangle }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="space-y-6">
            {/* User Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-6">
                <User className="h-8 w-8 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Account-Informationen</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="h-4 w-4 inline mr-2" />
                    E-Mail-Adresse
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {user.email || 'Nicht verfügbar'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Letzter Login
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString('de-DE') : 'Nicht verfügbar'}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Konto-Aktionen</h3>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleLogout}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Abmelden
                </button>
                
                <button
                  onClick={() => navigate('/qr/dash')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Zum Dashboard
                </button>
              </div>
            </div>
          </div>
        );

      case 'subscription':
        return (
          <div className="space-y-6">
            {/* Subscription Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-6">
                <CreditCard className="h-8 w-8 text-green-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Abonnement</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aktueller Plan</label>
                  <div className="flex items-center">
                    {subscription?.plan !== 'free' && <Crown className="h-4 w-4 text-yellow-500 mr-2" />}
                    <span className="text-lg font-semibold text-gray-900">
                      {subscription ? getPlanDisplayName(subscription.plan) : 'Lädt...'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <div className="flex items-center">
                    <StatusIcon className={`h-4 w-4 mr-2 ${statusDisplay.color}`} />
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusDisplay.bg} ${statusDisplay.color}`}>
                      {statusDisplay.text}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {subscription?.cancel_at_period_end ? 'Läuft ab am' : 'Nächste Abrechnung'}
                  </label>
                  <p className="text-sm text-gray-900">
                    {subscription?.current_period_end ? 
                      new Date(subscription.current_period_end).toLocaleDateString('de-DE') : 
                      'Nicht verfügbar'}
                  </p>
                </div>
              </div>

              {/* Plan Limits Display */}
              {planLimits && (
                <div className="space-y-4">
                  {/* Team Info Banner */}
                  {subscription?.team_info?.is_team_member && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <Crown className="h-5 w-5 text-blue-600 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">
                            Team-Mitglied ({subscription.team_info.user_role})
                          </p>
                          <p className="text-xs text-blue-700">
                            Sie nutzen das {getPlanDisplayName(subscription.plan)}-Abonnement Ihres Teams 
                            ({subscription.team_info.team_size} Mitglieder)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Usage Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {subscription?.qr_codes_used || 0}/{planLimits.qr_codes_limit === -1 ? '∞' : planLimits.qr_codes_limit}
                      </p>
                      <p className="text-xs text-gray-600">
                        QR-Codes{subscription?.team_info?.is_team_member ? ' (Team)' : ''}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {subscription?.books_used || 0}/{planLimits.books_limit === -1 ? '∞' : planLimits.books_limit}
                      </p>
                      <p className="text-xs text-gray-600">
                        Bücher{subscription?.team_info?.is_team_member ? ' (Team)' : ''}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {subscription?.campaigns_used || 0}/{planLimits.campaigns_limit === -1 ? '∞' : planLimits.campaigns_limit}
                      </p>
                      <p className="text-xs text-gray-600">
                        Kampagnen{subscription?.team_info?.is_team_member ? ' (Team)' : ''}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-600">
                        {subscription?.seats_used || 1}/{planLimits.seats_limit === -1 ? '∞' : planLimits.seats_limit}
                      </p>
                      <p className="text-xs text-gray-600">
                        {subscription?.team_info?.is_team_member ? 'Team-Mitglieder' : 'Benutzer'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Subscription Actions */}
              <div className="flex flex-wrap gap-3 mt-6">
                {subscription?.plan !== 'free' && (
                  <>
                    <button
                      onClick={handleManageBilling}
                      disabled={loading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {loading && <RefreshCw className="h-4 w-4 animate-spin" />}
                      Zahlungen verwalten
                    </button>
                    

                    
                    {!subscription?.cancel_at_period_end && (
                      <button
                        onClick={() => setShowCancelModal(true)}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        Abonnement kündigen
                      </button>
                    )}
                  </>
                )}
                
                {subscription?.plan === 'free' && (
                  <button
                    onClick={() => navigate('/qr/pricing')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                  >
                    <Crown className="h-4 w-4 mr-2" />
                    Jetzt upgraden
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      // case 'url':
      //   return <AccountSlugSettings />; // Temporarily disabled

      case 'branding':
        return <BrandingSettings />;

      case 'danger':
        return (
          <div className="space-y-6">
            {/* Danger Zone */}
            <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6 border-l-4 border-l-red-500">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h2 className="text-xl font-bold text-red-600">Gefahrenbereich</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-red-800 mb-2">Account dauerhaft löschen</h3>
                  <div className="text-sm text-red-700 mb-4 space-y-2">
                    <p>⚠️ <strong>Warnung:</strong> Diese Aktion kann nicht rückgängig gemacht werden.</p>
                    
                    {/* Team-specific warnings */}
                    {subscription?.plan !== 'free' && (
                      <p>• Ihr Abonnement wird sofort gekündigt.</p>
                    )}
                    
                    <div className="bg-red-100 p-3 rounded border-l-4 border-red-400 mt-3">
                      <p className="font-medium text-red-800 mb-1">Was passiert bei der Löschung:</p>
                      <ul className="text-xs text-red-700 space-y-1">
                        <li>• <strong>Team-Mitglied/Admin:</strong> Sie werden aus dem Team entfernt, aber Ihre QR-Codes und Bücher bleiben erhalten</li>
                        <li>• <strong>Team-Owner mit anderen Admins:</strong> Ein Admin wird zum neuen Owner befördert, Ihre Daten bleiben erhalten</li>
                        <li>• <strong>Team-Owner ohne andere Admins:</strong> Das gesamte Team und alle Daten werden gelöscht</li>
                        <li>• <strong>Einzelnutzer:</strong> Alle Ihre Daten werden dauerhaft gelöscht</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Account löschen
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center">
          <User className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Profil</h1>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isDisabled = tab.disabled;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => !isDisabled && setActiveTab(tab.id)}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${isActive 
                      ? 'border-blue-500 text-blue-600' 
                      : isDisabled
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                  disabled={isDisabled}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.label}
                  {tab.badge && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Abonnement kündigen</h3>
            <p className="text-gray-600 mb-4">
              Möchten Sie Ihr Abonnement wirklich kündigen? Sie haben weiterhin Zugang bis zum Ende der aktuellen Abrechnungsperiode.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grund für die Kündigung (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Teilen Sie uns mit, warum Sie kündigen..."
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={loading}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Kündige...' : 'Kündigen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Account dauerhaft löschen</h3>
            <div className="text-gray-600 mb-4 space-y-3">
              <p>⚠️ <strong>Warnung:</strong> Diese Aktion kann nicht rückgängig gemacht werden.</p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm font-medium text-yellow-800 mb-2">Je nach Ihrer Team-Rolle passiert folgendes:</p>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• <strong>Team-Mitglied:</strong> Entfernung aus Team, Ihre Daten bleiben erhalten</li>
                  <li>• <strong>Team-Owner:</strong> Wenn andere Admins vorhanden sind, wird einer befördert</li>
                  <li>• <strong>Letzter Team-Owner:</strong> Gesamtes Team wird gelöscht</li>
                  <li>• <strong>Einzelnutzer:</strong> Alle Daten werden gelöscht</li>
                </ul>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geben Sie "LÖSCHEN" ein, um zu bestätigen:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="LÖSCHEN"
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loading || deleteConfirmText !== 'LÖSCHEN'}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Lösche...' : 'Account löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};